<!-- ─── Toolbar: quick-add input injected into the page sub-header ─────────── -->
<ng-template #toolbarSearch>
  <div class="flex gap-2 w-full">
    <div class="relative flex-1">
      <ui-input
        type="text"
        [value]="quickAddText"
        (valueChange)="onQuickAddInput($event)"
        (keydown.enter)="quickAdd()"
        (focused)="onQuickAddFocus()"
        (blurred)="onQuickAddBlur()"
        placeholder="Add to list… e.g. 2x milk, 500g flour"
        [clearable]="true"
        (cleared)="clearQuickAdd()"
        [fullWidth]="true"
        ariaLabel="Quick-add product to shopping list"
        class="w-full">
      </ui-input>

      <!-- Autosuggest dropdown -->
      @if (showQuickAddSuggestions) {
        <ul
          class="absolute z-50 left-0 right-0 mt-1 max-h-80 overflow-y-auto rounded-box border border-base-200 bg-base-100 shadow-lg p-1">
          @for (s of quickAddSuggestions; track s.key) {
            <li>
              <button
                type="button"
                class="flex items-center gap-2 w-full rounded-lg px-2 py-1.5 text-left hover:bg-base-200 transition-colors"
                (mousedown)="$event.preventDefault()"
                (click)="selectSuggestion(s)">

                <!-- icon -->
                <span class="w-7 h-7 flex items-center justify-center shrink-0">
                  @if (s.product_key) {
                    <img
                      [src]="productIconUrl(s.product_key)"
                      [alt]="s.product_key"
                      class="max-w-full max-h-full object-contain"
                      loading="lazy" />
                  } @else {
                    <fa-icon [icon]="faCartShopping" class="text-base-content/30"></fa-icon>
                  }
                </span>

                <!-- title (subtitles intentionally omitted) -->
                <span class="flex-1 min-w-0 truncate text-sm">{{ s.title }}</span>

                <!-- colorized source badge -->
                <span class="badge badge-sm shrink-0" [ngClass]="suggestionBadgeClass(s.source)">
                  {{ suggestionLabel(s.source) }}
                </span>
              </button>
            </li>
          }
        </ul>
      }
    </div>

    <ui-button
      variant="primary"
      [disabled]="!quickAddText.trim()"
      (clicked)="quickAdd()">
      <fa-icon [icon]="faPlus"></fa-icon>
      <span class="hidden sm:inline ml-1">Add</span>
    </ui-button>
  </div>
</ng-template>


<!-- ─── Two-column page layout ─────────────────────────────────────────────── -->
<!-- 4 direct children auto-place into a 2×2 grid on desktop.
     DOM order = mobile order: shopping-list / out+low+freq / past-trips / export -->
<div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

  <!-- ════════════════════════════════════════════════════════════════════════
       [1] Shopping List  (desktop: col 1, row 1)
  ════════════════════════════════════════════════════════════════════════ -->
  <section class="space-y-4">

    <!-- List switcher + create (always visible) -->
    <div class="flex items-center justify-between gap-2 min-w-0">
      <div class="min-w-0 shrink">
        <app-shopping-list-switcher
          [lists]="lists"
          [activeListId]="activeList?.id"
          [householdId]="householdId"
          (listSelected)="switchList($event)"
          (listCreated)="onListCreated($event)"
          (listRenamed)="onListRenamed($event)"
          (listDeleted)="onListDeleted($event)">
        </app-shopping-list-switcher>
      </div>

      <!-- Summary counts on the right -->
      <div class="flex items-center gap-2 shrink-0">
        @if (totalCount > 0) {
          <span class="badge badge-neutral badge-sm">{{ pendingCount }} / {{ totalCount }}</span>
        }
      </div>
    </div>

    <!-- Loading state -->
    @if (loadingLists || loadingItems) {
      <div class="flex items-center gap-3 py-8 justify-center text-base-content/50">
        <span class="loading loading-spinner loading-md"></span>
        <span class="text-sm">Loading list…</span>
      </div>
    } @else if (totalCount === 0) {
      <!-- Empty state -->
      <div
        class="border border-dashed border-base-300 rounded-xl p-10 flex flex-col items-center gap-3 text-base-content/40 py-6">
        <fa-icon [icon]="faCartShopping" class="text-4xl"></fa-icon>
        <p class="text-sm text-center">Your shopping list is empty.<br>Use the input above to add items.</p>
      </div>
    } @else {

      <!-- Pending items grid -->
      @if (pendingCount > 0) {
        <div appAutoAnimate class="grid gap-2 shopping-grid-130">
          @for (item of pendingItems; track item.client_id) {
            <div>
              <div
                class="shopping-item-card shopping-item-pending group relative box-border border rounded-xl overflow-hidden cursor-pointer select-none w-full h-full p-2 flex flex-col"
                [class.opacity-60]="item.sync_status === 'pending_sync'"
                [class.border-error]="item.sync_status === 'failed'"
                role="checkbox"
                [attr.aria-checked]="false"
                tabindex="0"
                (click)="toggleItem(item)"
                (keydown.enter)="toggleItem(item)"
                (keydown.space)="toggleItem(item)">

                <!-- Sync-status badge (top-right) -->
                @if (item.sync_status === 'pending_sync') {
                  <span
                    class="absolute top-1 right-1 z-10 w-4 h-4 flex items-center justify-center"
                    title="Syncing…">
                    <span class="loading loading-spinner loading-xs text-base-content/40"></span>
                  </span>
                } @else if (item.sync_status === 'failed') {
                  <button
                    type="button"
                    class="absolute top-1 right-1 z-20 w-5 h-5 rounded-md bg-error/15 hover:bg-error hover:text-error-content text-error flex items-center justify-center"
                    [title]="item.last_sync_error ? 'Retry: ' + item.last_sync_error : 'Retry sync'"
                    (click)="$event.stopPropagation(); retryItem(item)"
                    (keydown.space)="$event.stopPropagation()">
                    <fa-icon [icon]="faRotateRight" class="text-[9px]"></fa-icon>
                  </button>
                }

                <!-- image -->
                <div class="shopping-item-image-wrap flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                  @if (item.product_key) {
                    @if (isIconLoading(item.client_id)) {
                      <div class="w-10 h-10 rounded-lg bg-base-300 animate-pulse"></div>
                    }
                    <img
                      #iconImg
                      [src]="productIconUrl(item.product_key, 'pending')"
                      [alt]="item.product_key"
                      class="shopping-item-icon max-h-full transition-opacity duration-300"
                      [class.opacity-0]="isIconLoading(item.client_id)"
                      [class.opacity-100]="!isIconLoading(item.client_id)"
                      loading="lazy"
                      (load)="onIconLoad(item.client_id, iconImg)"
                      (error)="onIconError(item.client_id, iconImg)" />
                  } @else if (item.sync_status === 'pending_sync') {
                    <span class="loading loading-spinner loading-sm text-base-content/30"></span>
                  } @else {
                    <fa-icon [icon]="faCartShopping" class="text-2xl opacity-40"></fa-icon>
                  }
                </div>

                <!-- text -->
                <div class="shrink-0 flex flex-col px-1 pb-1 pt-1">
                  <p class="shopping-item-title text-xs font-medium text-center px-1">
                    {{ item.title }}
                  </p>
                  @if (item.id !== undefined && item.id !== null) {
                    <input
                      type="text"
                      class="subtitle-edit shrink-0 mt-0.5 text-xs text-center w-full px-1 leading-tight truncate cursor-text"
                      placeholder="add note…"
                      [value]="item.subtitle ?? ''"
                      (focus)="onSubtitleFocus(item); $any($event.target).select()"
                      (blur)="onSubtitleBlur(item, $event)"
                      (keydown.enter)="$any($event.target).blur(); $event.stopPropagation()"
                      (keydown.escape)="onSubtitleEscape(item, $any($event))"
                      (click)="$event.stopPropagation()"
                      (keydown.space)="$event.stopPropagation()" />
                  } @else if (item.subtitle) {
                    <p class="subtitle-static text-xs leading-tight truncate mt-0.5 text-center">
                      {{ item.subtitle }}
                    </p>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      } @else if (doneCount === 0) {
        <!-- show nothing extra — handled by totalCount === 0 above -->
      }

      <!-- Done items grid -->
      @if (doneCount > 0) {
        <!-- Done action bar -->
        <div class="flex items-center gap-2 pt-1">
          <div class="flex-1 border-t border-base-200"></div>
          @if (activeTrip) {
            <ui-button
              variant="success"
              size="sm"
              [loading]="finishingTrip"
              [disabled]="finishingTrip"
              (clicked)="finishTrip()">
              <fa-icon [icon]="faFlag" class="mr-1"></fa-icon>
              Finish &amp; clear
            </ui-button>
          }
          <div class="flex-1 border-t border-base-200"></div>
        </div>

        <div appAutoAnimate class="grid gap-2 shopping-grid-135">
          @for (item of doneItems; track item.client_id) {
            <div>
              <div
                class="shopping-item-card shopping-item-done group relative box-border border rounded-xl overflow-hidden cursor-pointer select-none w-full h-full p-2 flex flex-col"
                role="checkbox"
                [attr.aria-checked]="true"
                tabindex="0"
                (click)="toggleItem(item)"
                (keydown.enter)="toggleItem(item)"
                (keydown.space)="toggleItem(item)">

                <div class="absolute inset-0 pointer-events-none z-10"></div>

                <!-- image -->
                <div class="shopping-item-image-wrap flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                  @if (item.product_key) {
                    @if (isIconLoading(item.client_id)) {
                      <div class="w-10 h-10 rounded-lg bg-base-300 animate-pulse"></div>
                    }
                    <img
                      #iconImg
                      [src]="productIconUrl(item.product_key, 'done')"
                      [alt]="item.product_key"
                      class="shopping-item-icon max-h-full transition-opacity duration-300"
                      [class.opacity-0]="isIconLoading(item.client_id)"
                      [class.opacity-100]="!isIconLoading(item.client_id)"
                      loading="lazy"
                      (load)="onIconLoad(item.client_id, iconImg)"
                      (error)="onIconError(item.client_id, iconImg)" />
                  } @else {
                    <fa-icon [icon]="faCartShopping" class="text-2xl opacity-40"></fa-icon>
                  }
                </div>

                <!-- text -->
                <div class="shrink-0 flex flex-col px-2 pb-2 pt-1">
                  <p class="shopping-item-title text-xs font-medium text-center">
                    {{ item.title }}
                  </p>
                  @if (item.subtitle) {
                    <p class="text-xs leading-tight truncate mt-1 text-center opacity-70 shrink-0">
                      {{ item.subtitle }}
                    </p>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }
    }

  </section>

  <!-- ════════════════════════════════════════════════════════════════════════
       [2] Suggestions: Out of Stock + Low Stock + Frequently Bought
           (desktop: col 2, row 1)
  ════════════════════════════════════════════════════════════════════════ -->
  <section class="space-y-6">

    <!-- Out of Stock -->
    <div class="space-y-3">
      <div class="flex items-baseline justify-between gap-2 flex-wrap">
        <div class="flex items-baseline gap-2">
          <h3 class="text-base font-semibold">Out of Stock</h3>
          @if (outOfStockProducts.length > 0) {
            <span class="badge badge-neutral badge-sm">{{ outOfStockProducts.length }}</span>
          }
        </div>
        <div class="flex items-center gap-2">
          @if (outOfStockProducts.length > 0) {
            <ui-button
              variant="ghost"
              size="sm"
              [loading]="addingAllOutOfStock"
              [disabled]="addingAllOutOfStock"
              (clicked)="addAllToList(outOfStockProducts, 'out')">
              <fa-icon [icon]="faLayerGroup" class="mr-1"></fa-icon>
              Add all
            </ui-button>
          }
        </div>
      </div>

      @if (loadingOutOfStock) {
        <div class="flex items-center gap-3 py-6 justify-center text-base-content/50">
          <span class="loading loading-spinner loading-md"></span>
        </div>
      } @else if (outOfStockProducts.length === 0) {
        <div class="border border-dashed border-base-300 rounded-xl px-4 py-6 flex flex-col items-center gap-2 text-base-content/40">
          <p class="text-xs text-center">No out-of-stock products.</p>
        </div>
      } @else {
        <div class="grid gap-2 shopping-grid-135">
          @for (p of outOfStockProducts; track p.id) {
            <div>
              <div
                class="group relative box-border border rounded-xl overflow-hidden cursor-pointer select-none w-full h-full bg-base-100 p-2 flex flex-col transition-colors"
                [class.border-base-300]="!isProductAdded(p)"
                [class.border-success]="isProductAdded(p)"
                [class.opacity-70]="isProductAdded(p)"
                [class.brightness-110]="isProductAdded(p)"
                role="button"
                tabindex="0"
                [title]="isProductAdded(p) ? 'Already added' : 'Add to shopping list'"
                (click)="addProductToList(p)"
                (keydown.enter)="addProductToList(p)"
                (keydown.space)="$event.preventDefault(); addProductToList(p)">

                @if (isProductAdded(p)) {
                  <div class="absolute inset-0 bg-success/10 pointer-events-none z-10 flex items-start justify-end p-1">
                    <span class="badge badge-success badge-xs">✓</span>
                  </div>
                }

                <!-- hover: link to product details -->
                <a
                  [routerLink]="['/products', p.id]"
                  class="absolute top-1 left-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-150
                         w-5 h-5 rounded-md bg-base-300/80 hover:bg-primary hover:text-primary-content
                         flex items-center justify-center text-base-content/60"
                  title="View product details"
                  (click)="$event.stopPropagation()">
                  <fa-icon [icon]="faArrowUpRightFromSquare" class="text-[9px]"></fa-icon>
                </a>

                <!-- image -->
                <div class="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                  @if (isProdIconLoading(p.id!)) {
                    <div class="w-10 h-10 rounded-lg bg-base-300 animate-pulse"></div>
                  }
                  <img
                    #prodImg
                    [src]="productThumbUrl(p)"
                    [alt]="p.name"
                    class="shopping-item-icon max-h-full transition-opacity duration-300"
                    [class.opacity-0]="isProdIconLoading(p.id!)"
                    [class.opacity-100]="!isProdIconLoading(p.id!)"
                    loading="lazy"
                    (load)="onProdIconLoad(p.id!, prodImg)"
                    (error)="onProdIconError(p.id!, prodImg)" />
                </div>

                <!-- text -->
                <div class="shrink-0 flex flex-col px-1 pb-1 pt-1">
                  <p class="text-xs font-medium leading-tight line-clamp-1 text-center px-1">
                    {{ p | productName }}
                  </p>
                  @if (p.quantity_as_text) {
                    <p class="text-xs text-base-content/50 leading-tight truncate text-center mt-0.5">
                      {{ p.quantity_as_text }}
                    </p>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Low Stock -->
    <div class="space-y-3">
      <div class="flex items-baseline justify-between gap-2 flex-wrap">
        <div class="flex items-baseline gap-2">
          <h3 class="text-base font-semibold">Low Stock</h3>
          @if (lowStockProducts.length > 0) {
            <span class="badge badge-neutral badge-sm">{{ lowStockProducts.length }}</span>
          }
        </div>
        <div class="flex items-center gap-2">
          @if (lowStockProducts.length > 0) {
            <ui-button
              variant="ghost"
              size="sm"
              [loading]="addingAllLowStock"
              [disabled]="addingAllLowStock"
              (clicked)="addAllToList(lowStockProducts, 'low')">
              <fa-icon [icon]="faLayerGroup" class="mr-1"></fa-icon>
              Add all
            </ui-button>
          }
        </div>
      </div>

      @if (loadingLowStock) {
        <div class="flex items-center gap-3 py-6 justify-center text-base-content/50">
          <span class="loading loading-spinner loading-md"></span>
        </div>
      } @else if (lowStockProducts.length === 0) {
        <div class="border border-dashed border-base-300 rounded-xl px-4 py-6 flex flex-col items-center gap-2 text-base-content/40">
          <p class="text-xs text-center">No low-stock products.</p>
        </div>
      } @else {
        <div class="grid gap-2 shopping-grid-135">
          @for (p of lowStockProducts; track p.id) {
            <div>
              <div
                class="group relative box-border border rounded-xl overflow-hidden cursor-pointer select-none w-full h-full bg-base-100 p-2 flex flex-col transition-colors"
                [class.border-base-300]="!isProductAdded(p)"
                [class.border-success]="isProductAdded(p)"
                [class.opacity-70]="isProductAdded(p)"
                role="button"
                tabindex="0"
                [title]="isProductAdded(p) ? 'Already added' : 'Add to shopping list'"
                (click)="addProductToList(p)"
                (keydown.enter)="addProductToList(p)"
                (keydown.space)="$event.preventDefault(); addProductToList(p)">

                @if (isProductAdded(p)) {
                  <div class="absolute inset-0 bg-success/10 pointer-events-none z-10 flex items-start justify-end p-1">
                    <span class="badge badge-success badge-xs">✓</span>
                  </div>
                }

                <!-- hover: link to product details -->
                <a
                  [routerLink]="['/products', p.id]"
                  class="absolute top-1 left-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-150
                         w-5 h-5 rounded-md bg-base-300/80 hover:bg-primary hover:text-primary-content
                         flex items-center justify-center text-base-content/60"
                  title="View product details"
                  (click)="$event.stopPropagation()">
                  <fa-icon [icon]="faArrowUpRightFromSquare" class="text-[9px]"></fa-icon>
                </a>

                <!-- image -->
                <div class="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                  @if (isProdIconLoading(p.id!)) {
                    <div class="w-10 h-10 rounded-lg bg-base-300 animate-pulse"></div>
                  }
                  <img
                    #prodImg
                    [src]="productThumbUrl(p)"
                    [alt]="p.name"
                    class="shopping-item-icon max-h-full transition-opacity duration-300"
                    [class.opacity-0]="isProdIconLoading(p.id!)"
                    [class.opacity-100]="!isProdIconLoading(p.id!)"
                    loading="lazy"
                    (load)="onProdIconLoad(p.id!, prodImg)"
                    (error)="onProdIconError(p.id!, prodImg)" />
                </div>

                <!-- text -->
                <div class="shrink-0 flex flex-col px-1 pb-1 pt-1">
                  <p class="text-xs font-medium leading-tight line-clamp-1 text-center px-1">
                    {{ p | productName }}
                  </p>
                  @if (p.quantity_as_text) {
                    <p class="text-xs text-base-content/50 leading-tight truncate text-center mt-0.5">
                      {{ p.quantity_as_text }}
                    </p>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Frequently Bought -->
    <div class="space-y-3">
      <div class="flex items-baseline gap-2">
        <h3 class="text-base font-semibold">Frequently Bought</h3>
        @if (frequentItems.length > 0) {
          <span class="badge badge-neutral badge-sm">{{ frequentItems.length }}</span>
        }
      </div>

      @if (loadingFrequent) {
        <div class="flex items-center gap-3 py-6 justify-center text-base-content/50">
          <span class="loading loading-spinner loading-md"></span>
        </div>
      } @else if (frequentItems.length === 0) {
        <div class="border border-dashed border-base-300 rounded-xl px-4 py-6 flex flex-col items-center gap-2 text-base-content/40">
          <p class="text-xs text-center">No frequently-bought items yet — they appear once you complete a few shopping trips.</p>
        </div>
      } @else {
        <div class="grid gap-2 shopping-grid-135">
          @for (item of frequentItems; track item.title + '|' + (item.product_id ?? '') + '|' + (item.product_key ?? '')) {
            <div>
              <div
                class="group relative box-border border border-base-300 rounded-xl overflow-hidden cursor-pointer select-none w-full h-full bg-base-100 p-2 flex flex-col transition-colors"
                role="button"
                tabindex="0"
                title="Add to shopping list"
                (click)="addFrequentToList(item)"
                (keydown.enter)="addFrequentToList(item)"
                (keydown.space)="$event.preventDefault(); addFrequentToList(item)">

                <!-- buy-count badge -->
                <div class="absolute top-1 right-1 z-10">
                  <span class="badge badge-neutral badge-xs">×{{ item.buy_count }}</span>
                </div>

                <!-- image / icon -->
                <div class="flex-1 min-h-0 h-20 flex items-center justify-center overflow-hidden">
                  @if (frequentIconUrl(item); as iconUrl) {
                    <img
                      [src]="iconUrl"
                      [alt]="item.title"
                      class="h-16 w-16 object-contain"
                      loading="lazy" />
                  } @else {
                    <fa-icon [icon]="faCartShopping" class="text-4xl text-base-content/30"></fa-icon>
                  }
                </div>

                <!-- text -->
                <div class="shrink-0 flex flex-col px-1 pb-1 pt-1">
                  <p class="text-xs font-medium leading-tight line-clamp-1 text-center px-1">
                    {{ item.title }}
                  </p>
                  @if (item.subtitle) {
                    <p class="text-xs text-base-content/50 leading-tight truncate text-center mt-0.5">
                      {{ item.subtitle }}
                    </p>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>

  </section>

  <!-- ════════════════════════════════════════════════════════════════════════
       [3] Past Trips  (desktop: col 1, row 2)
  ════════════════════════════════════════════════════════════════════════ -->
  @if (completedTrips.length > 0) {
    <section class="space-y-2">
      <div class="flex items-center gap-2">
        <div class="flex-1 border-t border-base-200"></div>
        <span class="text-xs text-base-content/40">Past trips</span>
        <div class="flex-1 border-t border-base-200"></div>
      </div>

      <div class="past-trips-list border border-base-200 rounded-xl overflow-hidden divide-y divide-base-200">
        @for (trip of completedTrips; track trip.id) {
          <ui-collapse
            icon="arrow"
            [isOpen]="isTripOpen(trip)"
            (isOpenChange)="onTripCollapseChange(trip, $event)">

            <div collapse-title class="flex items-center justify-between gap-2 text-sm py-0 min-h-0">
              <span class="font-medium">{{ trip.completed_at | date:'mediumDate' }}</span>
              <span class="text-xs text-base-content/40">{{ trip.completed_at | date:'shortTime' }}</span>
            </div>

            @if (tripDetailsLoading[trip.id]) {
              <div class="flex items-center gap-2 py-3 justify-center text-base-content/40">
                <span class="loading loading-spinner loading-sm"></span>
                <span class="text-xs">Loading items…</span>
              </div>
            } @else if (tripDetailsMap[trip.id]?.items?.length) {
              <div class="grid gap-2 pt-1 pb-2 shopping-grid-88">
                @for (tripItem of tripDetailsMap[trip.id].items!; track tripItem.id) {
                  <div
                    class="shopping-item-trip relative box-border border rounded-xl overflow-hidden cursor-pointer select-none p-1.5 flex flex-col items-center justify-center gap-1 transition-colors"
                    role="button"
                    tabindex="0"
                    [title]="'Add ' + tripItem.title + ' back to list'"
                    (click)="reAddTripItem(tripItem)"
                    (keydown.enter)="reAddTripItem(tripItem)"
                    (keydown.space)="reAddTripItem(tripItem)">

                    @if (tripItem.product_key) {
                      <img
                        [src]="productIconUrl(tripItem.product_key)"
                        [alt]="tripItem.product_key"
                        class="shopping-item-icon w-8 h-8 object-contain shrink-0"
                        loading="lazy" />
                    } @else {
                      <fa-icon [icon]="faCartShopping" class="text-base text-base-content/20"></fa-icon>
                    }

                    <p class="text-xs font-medium leading-tight line-clamp-2 text-center w-full">
                      {{ tripItem.title }}
                    </p>
                  </div>
                }
              </div>
            } @else if (tripDetailsMap[trip.id]) {
              <p class="text-xs text-base-content/40 py-3 text-center">No items recorded for this trip.</p>
            }
          </ui-collapse>
        }
      </div>
    </section>
  }

  <!-- ════════════════════════════════════════════════════════════════════════
       [4] Export  (desktop: col 2, row 2)
  ════════════════════════════════════════════════════════════════════════ -->
  <section class="space-y-3">
    <div class="flex items-baseline justify-between gap-2">
      <h3 class="text-base font-semibold">Export</h3>
      <a class="link link-primary text-sm" [routerLink]="['/shopping-list-export']">
        Manage
      </a>
    </div>

    @if (loadingExportLinks) {
      <div class="flex items-center gap-3 py-4 justify-center text-base-content/50">
        <span class="loading loading-spinner loading-sm"></span>
      </div>
    } @else if (exportLinksArray.length === 0) {
      <p class="text-sm text-base-content/50">
        No export providers configured. Set them up via <a class="link link-primary" [routerLink]="['/shopping-list-export']">Manage exports</a>.
      </p>
    } @else {
      <div class="flex flex-wrap gap-3">
        @for (link of exportLinksArray; track link.key) {
          <a
            [href]="resolveProviderAction(link.key, link.value)"
            target="_blank"
            rel="noopener noreferrer"
            class="group relative box-border border border-base-300 rounded-xl overflow-hidden cursor-pointer select-none bg-base-100 p-3 flex flex-col items-center justify-center gap-2 w-[112px] h-[112px] hover:border-primary hover:bg-primary/5 transition-colors"
            [title]="'Export to ' + providerLabel(link.key)">

            <!-- logo or fallback icon -->
            <div class="w-12 h-12 flex items-center justify-center shrink-0">
              @if (providerLogoUrl(link.key)) {
                <img
                  [src]="providerLogoUrl(link.key)!"
                  [alt]="link.key"
                  class="w-full h-full object-contain rounded-lg" />
              } @else {
                <fa-icon [icon]="faLink" class="text-2xl text-base-content/40 group-hover:text-primary transition-colors"></fa-icon>
              }
            </div>

            <!-- label -->
            <p class="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
              {{ providerLabel(link.key) }}
            </p>
          </a>
        }
      </div>
    }
  </section>

</div>
